import React, {Component, PropTypes} from 'react';
import CSSModules from 'react-css-modules';
import Hammer from 'react-hammerjs';
import _ from 'lodash';
import Slide from './Slide';
import Caption from './Caption';
import {
    isMobile,
    isTablet,
    getActiveSlide,
} from '../../../common';
import styles from './slider.scss';

const navigationSettings = {
    widthDesktop: 384,
    widthTablet: 288,
};

const sliderSettings = {
    heightMobile: 170,
    heightDefault: 440,
    overlay: {
        captionHeightMobile: 72,
        captionHeightTablet: 136,
        captionHeightDesktop: 168,
    },
    gutter: 8,
};

class Slider extends Component {

    static propTypes = {
        showCaptions: PropTypes.bool,
        onClick: PropTypes.func,
        isOverlay: PropTypes.bool,
        object: PropTypes.object,
        multimediaWidths: PropTypes.array,
        multimediaCount: PropTypes.number,
        isNavigationActive: PropTypes.bool,
        activeSlide: PropTypes.number,
        trackActiveSlide: PropTypes.func,
    };

    static defaultProps = {
        showCaptions: true,
        isOverlay: false,
        multimediaWidths: [],
        activeSlide: 0,
        trackActiveSlide: () => {},
    };

    constructor(props) {
        super(props);
        this.state = {
            mediaWidths: [],
            activeSlide: this.props.activeSlide,
            slidesCount: 0,
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.setMediaWidth);
        this.setMediaWidth();

        const activeSlide = getActiveSlide();
        if (activeSlide) {
            this.activateSlide(activeSlide);
        }

        const queryParams = window.location.hash.split('?')[1];
        if (queryParams.indexOf('target=slider') > -1) {
            const slider = document.getElementById('slider');
            slider.scrollIntoView();
        }
    }

    componentWillReceiveProps(nextProps) {
        const activeSlide = nextProps.activeSlide;

        if (activeSlide && activeSlide > 0 && this.state.activeSlide !== activeSlide) {
            this.setState({ activeSlide: activeSlide });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.setMediaWidth);
    }

    setMediaWidth = () => {
        let slidesCount = 0;
        const mediaWidths = this.props.multimediaWidths.map((dimensions) => {
            const proportion = dimensions[0]/dimensions[1];
            const availableWidth = this.getAvailableWidth();
            let width = proportion*this.getMaxHeight();

            if (width > availableWidth) {
                width = availableWidth;
            }

            const height = width/proportion;
            slidesCount = slidesCount + 1;

            return [width, height];
        });

        this.setState({
            slidesCount,
            mediaWidths: mediaWidths,
        });
    };

    getAvailableWidth = () => {
        let availableWidth = window.innerWidth - (sliderSettings.gutter*2);

        if (!isMobile() && this.props.isNavigationActive) {
            const navWidth = isTablet() ? navigationSettings.widthTablet : navigationSettings.widthDesktop;
            availableWidth = availableWidth - navWidth;
        }
        return availableWidth;
    };

    getMaxHeight = () => {
        let maxHeight;
        const { isOverlay } = this.props;
        if (!isOverlay) {
            maxHeight = isMobile() ? sliderSettings.heightMobile : sliderSettings.heightDefault;
        } else {
            // in overlay mode we need to calculate: available height = windowHeight - captionHeight
            if (isMobile()) {
                maxHeight = window.innerHeight - sliderSettings.overlay.captionHeightMobile;
            } else if (isTablet()) {
                maxHeight = window.innerHeight - sliderSettings.overlay.captionHeightTablet;
            } else {
                maxHeight = window.innerHeight - sliderSettings.overlay.captionHeightDesktop;
            }
        }
        return maxHeight;
    };

    getSlides = () => {
        const { object } = this.props;
        const { mediaWidths, activeSlide } = this.state;
        let slides = [];

        if (!_.isEmpty(mediaWidths)) {
            slides = object.ObjMultimedia.edges.map((edge,idx) => {
                const style = {
                    position: 'relative',
                    width: `${mediaWidths[idx][0]}px`,
                    height: `${mediaWidths[idx][1]}px`,
                    margin: `0 ${sliderSettings.gutter}px`,
                };
                return (
                    <div style={style} key={idx}>
                        <Slide
                            objId={object.id}
                            media={edge.node}
                            isOverlay={this.props.isOverlay}
                            isActive={activeSlide === idx}
                            maxHeight={this.getMaxHeight()}
                            activeSlide={this.state.activeSlide}
                        />
                    </div>
                );
            });
        }

        return slides;
    };

    activateSlide = (idx) => {
        this.setState({
            activeSlide: idx,
        });
        this.props.trackActiveSlide(idx);
    };

    getSliderPositionX = () => {
        const { activeSlide, mediaWidths} = this.state;
        let pos = 0;
        if (!_.isEmpty(mediaWidths)) {
            for (let i = 0; i < activeSlide; i++) {
                pos += mediaWidths[i][0] + sliderSettings.gutter*2;
            }
        }
        return pos;
    };

    getSliderOffset = () => {
        let offset;
        const availableWidth = this.getAvailableWidth();
        const tabletWidth = 768;

        if (availableWidth < tabletWidth) {
            offset = 0;
        } else {
            const imageWidth = _.get(this.state.mediaWidths, `[${this.state.activeSlide}][0]`, 0);
            offset = Math.round(availableWidth/2 - imageWidth/2);
        }

        return offset;
    };

    getNavItems = () => {
        const { multimediaCount } = this.props;
        const { activeSlide } = this.state;
        const navItems = [];
        for(let i = 0; i < multimediaCount; i++) {
            navItems.push(
                <li
                    key={i}
                    styleName={i === activeSlide ? 'nav-item-active' : 'nav-item'}
                    onClick={() => this.activateSlide(i)}
                />
            )
        }
        return navItems;
    };

    getPrevSlideIndex = () => {
        let prevSlideIdx = this.state.activeSlide - 1;
        if (prevSlideIdx < 0) {
            prevSlideIdx = 0;
        }

        return prevSlideIdx;
    };

    getNextSlideIndex = () => {
        let nextSlideIdx = this.state.activeSlide + 1;
        if (nextSlideIdx > this.state.slidesCount - 1) {
            nextSlideIdx = this.state.activeSlide;
        }
        return nextSlideIdx;
    };

    handleSwipe = (args) => {
        const dir = args.deltaX;
        const slidesCount = this.props.object.ObjMultimedia.edges.length;

        if (dir > 0 && this.activeSlideIdx > 0) {
            const prevSlideIdx = this.getPrevSlideIndex();
            this.activateSlide(prevSlideIdx);
        } else if( dir < 0 && this.activeSlideIdx < slidesCount - 1) {
            const nextSlideIdx = this.getNextSlideIndex();
            this.activateSlide(nextSlideIdx);
        }
    };

    render() {
        const { object } = this.props;
        let captions = object.ObjMultimedia.edges.map((edge,idx) => {
            return(
                <Caption
                    key={idx}
                    media={ edge.node }
                    activeSlide={this.state.activeSlide}
                    slideIndex={idx}
                />
            );
        });

        return (
            <div id="slider" style={{position: 'relative', paddingTop: `${this.getMaxHeight()}px`}}>
                <Hammer onSwipe={ this.handleSwipe } onClick={ this.props.onClick ? this.props.onClick : null}>
                    <div
                        styleName="slider"
                        style={{
                            marginLeft: `${this.getSliderOffset()}px`,
                            transform: `translateX(-${ this.getSliderPositionX() }px)`
                        }}
                    >
                        { this.getSlides() }
                    </div>
                </Hammer>
                <ul styleName="nav">
                    <li
                        styleName="nav-item-prev"
                        onClick={ () => this.activateSlide(this.getPrevSlideIndex()) }
                    />
                    { this.getNavItems() }
                    <li
                        styleName="nav-item-next"
                        onClick={ () => this.activateSlide(this.getNextSlideIndex()) }
                    />
                </ul>
                <div styleName="captions">
                    <div styleName="inner">
                        {captions}
                    </div>
                </div>
            </div>
        )
    }
}

export default CSSModules(Slider, styles);



// WEBPACK FOOTER //
// ./app/components/Object/Slider/Slider.jsx